import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type UseFormBlockerParams = {
  shouldBlockNavigation: boolean;
  onConfirm?: () => void;
};

type UseFormBlockerReturn = {
  unblockedNavigate: (pathOrDelta: string | number) => void;
};

export const useFormBlocker = ({
  shouldBlockNavigation,
  onConfirm,
}: UseFormBlockerParams): UseFormBlockerReturn => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const isNavigationExpectedRef = useRef(false);

  const locationPathname = location.pathname;

  useEffect(() => {
    const unblock = history.block((promptLocation) => {
      const newLocationPathname = promptLocation.pathname;
      if (
        locationPathname !== newLocationPathname &&
        shouldBlockNavigation &&
        !isNavigationExpectedRef.current &&
        // do not block redirection to support us page
        !newLocationPathname.includes('support_us')
      ) {
        // eslint-disable-next-line no-alert
        if (window.confirm(t('forms.unsavedChanges'))) {
          unblock();
          if (onConfirm) onConfirm();
          history.push(newLocationPathname);
        } else {
          return false;
        }
      }
      return undefined;
    });

    return () => {
      unblock();
      // Reset on unmount to prevent stale state
      isNavigationExpectedRef.current = false;
    };
  }, [history, locationPathname, onConfirm, shouldBlockNavigation, t]);

  /**
   * Navigates to a desired path without a blocker.
   * This is the preferred way to navigate after successful form submission,
   * as it ensures the blocker is disabled atomically with the navigation.
   * @param pathOrDelta - A string path to navigate to, or a number to go back/forward in history
   */
  const unblockedNavigate = useCallback(
    (pathOrDelta: string | number) => {
      isNavigationExpectedRef.current = true;
      if (typeof pathOrDelta === 'number') {
        // Best-effort check: history.length doesn't reflect our position in the stack, but if the delta exceeds
        // total length, it definitely can't work.
        if (Math.abs(pathOrDelta) < history.length) {
          history.go(pathOrDelta);
        } else {
          history.push('/');
        }
      } else {
        history.push(pathOrDelta);
      }
    },
    [history],
  );

  return { unblockedNavigate };
};
